import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import adminRoutes from "../routes/admin.routes";
import { DrawerProvider } from "../../../shared/providers/DrawerProvider";
import Drawer from "../../../shared/components/Drawer";
import Brand from "@/shared/components/ui/Brand";
import Button from "../../../shared/components/Button";
import PrimaryIcon from "@/shared/components/ui/PrimaryIcon";
import Icons from "../../../shared/constants/icon.constant";
import { X } from "lucide-react";

export default function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isSidebarOpen]);

  return (
    <DrawerProvider>
      <div className="h-screen flex text-white overflow-hidden">
        {/* Top Navbar */}
        <div className="fixed top-0 w-full border-b border-sidebar-active z-50 bg-main-dark">
          <div className="flex w-full flex-col md:flex-row">
            <div className="md:w-75 w-full">
              <div className="flex justify-between px-4 py-4 items-center">
                <Brand />
                <Button variant="icon" onClick={toggleSidebar}>
                  <PrimaryIcon
                    icon={Icons.Menu({ width: 10, height: 10 })}
                    isActive={false}
                  />
                </Button>
              </div>
            </div>
            <Navbar />
          </div>
        </div>

        {/* Desktop Sidebar */}
        <aside
          className={`hidden md:block fixed top-14 left-0 h-[calc(100vh-3.5rem)] z-30 will-change-transform transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-60"
          }`}>
          <Sidebar
            items={adminRoutes.children.filter(
              (item) => item.path !== "profile"
            )}
          />
        </aside>

        {/* Mobile Sidebar Overlay */}
        <div
          className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
            isSidebarOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={closeSidebar}
          aria-hidden="true"
        />

        {/* Mobile Sidebar */}
        <aside
          className={`md:hidden fixed top-0 left-0 h-full w-60 z-50 will-change-transform transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}>
          <div className="h-full bg-sidebar flex flex-col">
            {/* Mobile Sidebar Header */}
            <div className="flex justify-between items-center px-4 py-4 border-b border-sidebar-active">
              <Brand />
              <button
                onClick={closeSidebar}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Close sidebar">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Mobile Sidebar Content */}
            <Sidebar
              items={adminRoutes.children.filter(
                (item) => item.path !== "profile"
              )}
            />
          </div>
        </aside>

        {/* Main Content */}
        <div
          className={`flex flex-col flex-1 will-change-[margin] transition-[margin] duration-300 ease-in-out ${
            isSidebarOpen ? "md:ml-60" : "md:ml-0"
          }`}>
          <main className="mt-14 p-4 sm:p-6 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
      <Drawer />
    </DrawerProvider>
  );
}
